export type Lang = "he" | "en";

const translations = {
    en: {
        nav: {
            about: "About",
            services: "Services",
            partners: "Partners",
            contact: "Contact",
            langSwitch: "עברית",
        },
        mail: {
            consultation: "Free IT Consultation Request",
            booking: "Booking a Free IT Consultation",
            general: "New Enquiry — Getting Started with Syslogics",
        },
        hero: {
            label: "Israel's Premier IT Partner — Est. 2000+",
            titleLine1: "Empowering Business",
            titleLine2: "Through Technology",
            tagline:
                "Enterprise-grade IT solutions designed for forward-thinking companies. Cloud, security, infrastructure — scaled to your ambition.",
            cta1: "Get Free Consultation",
            cta2: "View Our Services",
        },
        about: {
            eyebrow: "Who We Are",
            title: "Your Trusted\nIT Partner",
            p1: "Founded with a vision to deliver tailored IT services that drive business success, Syslogics has become Israel's premier technology partner for forward-thinking companies.",
            p2: "Our expert team specializes in cloud solutions, infrastructure management, cybersecurity, and comprehensive support services across diverse industries with personalized, scalable technology strategies.",
            highlight:
                "Bilingual excellence — fluent in English and Hebrew, delivering culturally-aware solutions for international and local clients alike.",
            badgeLabel: "Years of\nExcellence",
            stat1Num: "70%",
            stat1Label: "Avg. downtime\nreduction",
            stat2Num: "100+",
            stat2Label: "Enterprise\nclients served",
        },
        services: {
            eyebrow: "What We Do",
            title: "Core Services",
            items: [
                {
                    num: "01",
                    title: "Managed IT Support",
                    desc: "Proactive maintenance, rapid troubleshooting, and 24/7 monitoring to keep your systems running at peak performance.",
                },
                {
                    num: "02",
                    title: "Cloud Computing",
                    desc: "Secure migration, hybrid cloud integration, and scalable hosting solutions tailored to your business requirements.",
                },
                {
                    num: "03",
                    title: "Software Development",
                    desc: "Custom applications, system integrations, and legacy modernization built around the way your business actually works.",
                },
                {
                    num: "04",
                    title: "Backup & Recovery",
                    desc: "Local and cloud backup solutions ensuring business continuity and rapid disaster recovery when you need it most.",
                },
                {
                    num: "05",
                    title: "Agentic AI",
                    desc: "Custom AI agents that do real work — triaging tickets, processing documents, and running multi-step workflows inside the systems you already use.",
                },
            ],
        },
        whyChoose: {
            eyebrow: "Why Syslogics",
            title: "Proven Results",
            stat1Label: "Years Experience",
            stat1Desc: "Supporting law firms, SMEs, and enterprise clients across diverse industries",
            stat2Label: "Downtime Reduction",
            stat2Desc: "Achieved for a Tel Aviv-based law firm through strategic cloud migration",
            quote: "Syslogics transformed our IT from a cost center to a growth enabler. Their proactive approach and deep expertise have been instrumental in our company’s digital transformation.",
            cite: "— Senior Partner, Leading Israeli Law Firm",
        },
        resources: {
            eyebrow: "Knowledge Hub",
            title: "Resources & Insights",
            items: [
                {
                    num: "01",
                    title: "Expert Blog",
                    desc: "Regular updates on IT trends, cybersecurity tips, and cloud innovations to keep you informed and ahead of the curve.",
                },
                {
                    num: "02",
                    title: "Case Studies",
                    desc: "Whitepapers showcasing real-world IT transformations and proven business results for clients like yours.",
                },
                {
                    num: "03",
                    title: "Learning Events",
                    desc: "Webinars and workshops available in both English and Hebrew for continuous professional education.",
                },
            ],
        },
        partners: {
            eyebrow: "Trusted By",
            title: "Israel's Leading\nProfessional Firms",
            intro:
                "For over two decades, Israel's foremost law firms, investment funds, and professional organizations have placed their trust in Syslogics to deliver the IT infrastructure that powers their success.",
            items: [
                { slug: "agmon",    ext: "svg", name: "Agmon with Tulchinsky",           desc: "220+ lawyers · Leading counsel for key players in the Israeli economy",    darken: false, url: "https://www.agmon-law.co.il" },
                { slug: "lipa",     ext: "svg", name: "Lipa & Co.",                    desc: "Est. 1987 · Comprehensive legal services across practice areas",            darken: false, url: "https://www.lipa.co.il" },
                { slug: "ebn",      ext: "png", name: "Erdinast, Ben Natan, Toledano & Co.", desc: "Top-ranked · Israel's fastest-growing full-service law firm",          darken: false, url: "https://www.ebnlaw.co.il" },
                { slug: "har",      ext: "png", name: "Hadad, Roth, Shenhar",          desc: "Commercial law and litigation counsel of distinction",                      darken: true,  url: "https://www.har.law" },
                { slug: "ysl",      ext: "png", name: "Salomon Lifshitz & Co.",        desc: "Est. 1923 · A century of legal excellence in Israel",                      darken: false, url: "https://www.ysl-law.com" },
                { slug: "zes",      ext: "svg", name: "Zemah Schneider & Co.",         desc: "Full-service law firm with a modern approach to legal excellence",          darken: false, url: "https://www.zes.co.il" },
                { slug: "shalom",   ext: "jpg", name: "Shalom Tel Aviv",               desc: "Strategic PR, communications & crisis management agency",                   darken: false, url: "https://www.shalomtelaviv.com" },
                { slug: "erm",      ext: "png", name: "Epstein Rosenblum Maoz",        desc: "Epstein Rosenblum Maoz · Full-service Israeli law firm",                    darken: false, url: "https://erm-law.com/en" },
                { slug: "",         ext: "png", name: "Banai Azriel Stern",            desc: "Established, distinctive & leading counsel across commercial and hi-tech",  darken: false, url: "https://bas-law.co.il" },
                { slug: "shimonov", ext: "svg", name: "Shimonov & Co.",                desc: "Israel's leading capital markets law firm · 30 years of excellence",        darken: false, url: "https://shimonov.com" },
                { slug: "tene",     ext: "svg", name: "Tene Capital",                  desc: "Israeli private equity · $1B+ across four funds, mid-market investments",   darken: false, url: "https://www.tenecapital.com" },
                { slug: "pontifax", ext: "png", name: "Pontifax",                      desc: "Life sciences VC · $1.2B invested in groundbreaking healthcare innovation", darken: true,  url: "https://www.pontifax.com" },
                { slug: "",         ext: "png", name: "KRB Law",                       desc: "Crispin Rubinstein Balcher Kadosh · Creative solutions, remarkable results", darken: false, url: "https://krb.law" },
                { slug: "heu",      ext: "svg", name: "HEU & Co.",                     desc: "Horovitz, Even, Uzan · Exceptional legal services since 2007",              darken: false, url: "https://www.heu-law.com" },
                { slug: "koresh",   ext: "svg", name: "Koresh & Co.",                  desc: "Tel Aviv advocates · Dedicated legal counsel across practice areas",         darken: true,  url: "https://koresh.law" },
                { slug: "efi",      ext: "png", name: "Efi Nativ",                     desc: "Entrepreneurship & real estate development · Innovation-driven growth",      darken: false, url: "https://www.efi-natif.co.il" },
                { slug: "noy",      ext: "png", name: "Noy Fund",                      desc: "Israeli venture capital · Investing in technology and innovation",           darken: false, url: "https://www.noyfund.co.il" },
            ],
        },
        contact: {
            eyebrow: "Get in Touch",
            title: "Let's Elevate\nYour IT",
            details: [
                { label: "Office Hours", value: "Sunday – Thursday, 09:00 – 18:00" },
                { label: "Address",      value: "8a Saknai St., Hod Hasharon, 4535308, Israel" },
                { label: "General",      value: "info@sys-it.co.il" },
                { label: "Support",      value: "help@sys-it.co.il" },
                { label: "Telephone",    value: "+972 3 644 0671" },
                { label: "Fax",          value: "+972 77 400 4913" },
            ],
            ctaTitle: "Ready to Transform\nYour Infrastructure?",
            ctaDesc:
                "Schedule a free consultation to assess your current systems and discover how Syslogics can accelerate your business growth.",
            ctaBtn: "Book Free Consultation",
            opt1: "English support",
            opt2: "Hebrew support",
            opt3: "On-site visits",
        },
        footer: {
            tagline: "Israel's Premier Enterprise IT Partner",
            nav: { about: "About", services: "Services", partners: "Partners", contact: "Contact" },
            pillars: [
                { title: "Innovation",     desc: "Cutting-edge solutions that keep you ahead of technology trends and competitive challenges." },
                { title: "Reliability",    desc: "Consistent performance and proactive support ensuring your systems work when you need them most." },
                { title: "Personalization",desc: "Tailored solutions and dedicated support that grows and adapts with your unique business needs." },
            ],
            copy: "© 2026 Syslogics. All rights reserved.",
            taglineBottom: "Experience IT That Works For You",
            getStarted: "Get Started Today",
        },
    },

    he: {
        nav: {
            about: "אודות",
            services: "שירותים",
            partners: "שותפים",
            contact: "צור קשר",
            langSwitch: "English",
        },
        mail: {
            consultation: "בקשה לייעוץ IT ללא עלות",
            booking: "קביעת ייעוץ IT ללא עלות",
            general: "פנייה חדשה — התחלת עבודה עם Syslogics",
        },
        hero: {
            label: "שותף ה-IT המוביל בישראל — פועלים מ-2000",
            titleLine1: "מעצימים עסקים",
            titleLine2: "באמצעות טכנולוגיה",
            tagline:
                "פתרונות IT ברמה ארגונית המיועדים לחברות חדשניות. ענן, אבטחה, תשתיות — בהתאם לשאיפות שלכם.",
            cta1: "קבלו ייעוץ חינם",
            cta2: "צפו בשירותים שלנו",
        },
        about: {
            eyebrow: "מי אנחנו",
            title: "שותף ה-IT\nהנאמן שלכם",
            p1: "נוסדה עם חזון לספק שירותי IT מותאמים המניעים הצלחה עסקית, Syslogics הפכה לשותפת הטכנולוגיה המובילה בישראל עבור חברות חדשניות.",
            p2: "הצוות המומחה שלנו מתמחה בפתרונות ענן, ניהול תשתיות, אבטחת סייבר ושירותי תמיכה מקיפים במגוון תעשיות עם אסטרטגיות טכנולוגיה מותאמות ומדרגיות.",
            highlight:
                "מצוינות דו-לשונית — שולטים בעברית ובאנגלית, מספקים פתרונות מודעי תרבות ללקוחות בינלאומיים ומקומיים כאחד.",
            badgeLabel: "שנות\nמצוינות",
            stat1Num: "70%",
            stat1Label: "הפחתת זמן\nהשבתה ממוצעת",
            stat2Num: "100+",
            stat2Label: "לקוחות ארגוניים\nשובשנו",
        },
        services: {
            eyebrow: "מה אנחנו עושים",
            title: "שירותים מרכזיים",
            items: [
                {
                    num: "01",
                    title: "תמיכת IT מנוהלת",
                    desc: "תחזוקה יזומה, פתרון תקלות מהיר וניטור 24/7 לשמירה על ביצועי שיא של המערכות שלכם.",
                },
                {
                    num: "02",
                    title: "מחשוב ענן",
                    desc: "העברה מאובטחת, אינטגרציית ענן היברידי ופתרונות אירוח מדרגיים המותאמים לצרכי העסק שלכם.",
                },
                {
                    num: "03",
                    title: "פיתוח תוכנה",
                    desc: "אפליקציות בהתאמה אישית, אינטגרציה בין מערכות ומודרניזציה של מערכות ותיקות — בהתאמה לאופן שבו העסק שלכם עובד באמת.",
                },
                {
                    num: "04",
                    title: "גיבוי ושחזור",
                    desc: "פתרונות גיבוי מקומי וענן המבטיחים המשכיות עסקית ושחזור מהיר מאסון כשצריך אותו ביותר.",
                },
                {
                    num: "05",
                    title: "בינה מלאכותית אג'נטית",
                    desc: "סוכני AI מותאמים אישית שמבצעים עבודה אמיתית — מיון פניות, עיבוד מסמכים והרצת תהליכים רב-שלביים בתוך המערכות שכבר קיימות אצלכם.",
                },
            ],
        },
        whyChoose: {
            eyebrow: "למה Syslogics",
            title: "תוצאות מוכחות",
            stat1Label: "שנות ניסיון",
            stat1Desc: "תמיכה במשרדי עורכי דין, עסקים קטנים ובינוניים ולקוחות ארגוניים במגוון תעשיות",
            stat2Label: "הפחתת זמן השבתה",
            stat2Desc: "הושג עבור משרד עורכי דין בתל אביב באמצעות העברה אסטרטגית לענן",
            quote: "Syslogics הפכה את ה-IT שלנו ממרכז עלויות למנוע צמיחה. הגישה היזומה שלהם והמומחיות העמוקה היו מכריעים בהתמרה הדיגיטלית של החברה שלנו.",
            cite: "— שותף בכיר, משרד עורכי דין ישראלי מוביל",
        },
        resources: {
            eyebrow: "מרכז הידע",
            title: "משאבים ותובנות",
            items: [
                {
                    num: "01",
                    title: "בלוג מקצועי",
                    desc: "עדכונים שוטפים על מגמות IT, טיפים לאבטחת סייבר וחידושי ענן כדי לשמור עליכם מעודכנים ובחוד החנית.",
                },
                {
                    num: "02",
                    title: "מקרי בוחן",
                    desc: "מסמכים לבנים המציגים מהפכות IT מהעולם האמיתי ותוצאות עסקיות מוכחות ללקוחות כמוכם.",
                },
                {
                    num: "03",
                    title: "אירועי למידה",
                    desc: "וובינרים וסדנאות בעברית ובאנגלית לחינוך מקצועי מתמשך.",
                },
            ],
        },
        partners: {
            eyebrow: "סומכים עלינו",
            title: "הארגונים המקצועיים\nהמובילים בישראל",
            intro:
                "במשך למעלה מעשרים שנה, משרדי עורכי הדין, קרנות ההשקעה והארגונים המקצועיים המובילים בישראל בוחרים בסיסלוג'יקס לספק את תשתיות ה-IT שמניעות את הצלחתם.",
            items: [
                { slug: "agmon",    ext: "svg", name: "Agmon with Tulchinsky",           desc: "220+ עורכי דין · עורכי דין מובילים עבור שחקני מפתח במשק הישראלי",     darken: false, url: "https://www.agmon-law.co.il" },
                { slug: "lipa",     ext: "svg", name: "Lipa & Co.",                    desc: "נוסד ב-1987 · שירותים משפטיים מקיפים בכל תחומי העיסוק",                darken: false, url: "https://www.lipa.co.il" },
                { slug: "ebn",      ext: "png", name: "Erdinast, Ben Natan, Toledano & Co.", desc: "מובילים בדירוג · משרד עורכי הדין הישראלי הצומח הכי מהיר",         darken: false, url: "https://www.ebnlaw.co.il" },
                { slug: "har",      ext: "png", name: "Hadad, Roth, Shenhar",          desc: "ייצוג מסחרי ועורכי דין לדין ייחודיים",                                  darken: true,  url: "https://www.har.law" },
                { slug: "ysl",      ext: "png", name: "Salomon Lifshitz & Co.",        desc: "נוסד ב-1923 · מאה שנות מצוינות משפטית בישראל",                          darken: false, url: "https://www.ysl-law.com" },
                { slug: "zes",      ext: "svg", name: "Zemah Schneider & Co.",         desc: "משרד עורכי דין מלא עם גישה מודרנית למצוינות משפטית",                    darken: false, url: "https://www.zes.co.il" },
                { slug: "shalom",   ext: "jpg", name: "Shalom Tel Aviv",               desc: "סוכנות אסטרטגית ליחסי ציבור, תקשורת וניהול משברים",                     darken: false, url: "https://www.shalomtelaviv.com" },
                { slug: "erm",      ext: "png", name: "Epstein Rosenblum Maoz",        desc: "אפשטיין רוזנבלום מאוז · משרד עורכי דין ישראלי מלא",                     darken: false, url: "https://erm-law.com/en" },
                { slug: "",         ext: "png", name: "Banai Azriel Stern",            desc: "משרד ותיק, ייחודי ומוביל · ייצוג בדיני מסחר, היי-טק ועוד",             darken: false, url: "https://bas-law.co.il" },
                { slug: "shimonov", ext: "svg", name: "Shimonov & Co.",                desc: "משרד עורכי הדין המוביל בישראל בשוק ההון · 30 שנות מצוינות",              darken: false, url: "https://shimonov.com" },
                { slug: "tene",     ext: "svg", name: "Tene Capital",                  desc: "קרן פרייבט אקוויטי ישראלית · מעל מיליארד דולר בארבעה קרנות",            darken: false, url: "https://www.tenecapital.com" },
                { slug: "pontifax", ext: "png", name: "Pontifax",                      desc: "קרן הון סיכון במדעי החיים · 1.2 מיליארד דולר בחדשנות בתחום הבריאות",   darken: true,  url: "https://www.pontifax.com" },
                { slug: "",         ext: "png", name: "KRB Law",                       desc: "קריספין רובינשטיין בלכר קדוש · פתרונות יצירתיים, תוצאות יוצאות דופן",  darken: false, url: "https://krb.law" },
                { slug: "heu",      ext: "svg", name: "HEU & Co.",                     desc: "הורוביץ, אבן, עוזן ושות' · שירותים משפטיים יוצאי דופן מאז 2007",        darken: false, url: "https://www.heu-law.com" },
                { slug: "koresh",   ext: "svg", name: "Koresh & Co.",                  desc: "עורכי דין בתל אביב · ייעוץ משפטי מסור בכל תחומי העיסוק",               darken: true,  url: "https://koresh.law" },
                { slug: "efi",      ext: "png", name: "Efi Nativ",                     desc: "יזמות ופיתוח נדל\"ן · צמיחה מונעת חדשנות",                              darken: false, url: "https://www.efi-natif.co.il" },
                { slug: "noy",      ext: "png", name: "Noy Fund",                      desc: "קרן הון סיכון ישראלית · השקעות בטכנולוגיה וחדשנות",                      darken: false, url: "https://www.noyfund.co.il" },
            ],
        },
        contact: {
            eyebrow: "צרו קשר",
            title: "בואו נשדרג\nאת ה-IT שלכם",
            details: [
                { label: "שעות פעילות", value: "ראשון – חמישי, 09:00 – 18:00" },
                { label: "כתובת",        value: "שקנאי 8א, הוד השרון, 4535308, ישראל" },
                { label: "כללי",         value: "info@sys-it.co.il" },
                { label: "תמיכה",        value: "help@sys-it.co.il" },
                { label: "טלפון",        value: "+972 3 644 0671" },
                { label: "פקס",          value: "+972 77 400 4913" },
            ],
            ctaTitle: "מוכנים להמיר\nאת התשתית שלכם?",
            ctaDesc:
                "קבעו ייעוץ חינמי להערכת המערכות הנוכחיות שלכם וגלו כיצד Syslogics יכולה להאיץ את צמיחת העסק שלכם.",
            ctaBtn: "קבעו ייעוץ חינמי",
            opt1: "תמיכה באנגלית",
            opt2: "תמיכה בעברית",
            opt3: "ביקורים באתר",
        },
        footer: {
            tagline: "שותפת ה-IT הארגוני המובילה בישראל",
            nav: { about: "אודות", services: "שירותים", partners: "שותפים", contact: "צור קשר" },
            pillars: [
                { title: "חדשנות",       desc: "פתרונות חדשניים המשמרים אתכם בחוד החנית של מגמות הטכנולוגיה ואתגרים תחרותיים." },
                { title: "אמינות",       desc: "ביצועים עקביים ותמיכה יזומה המבטיחים שהמערכות שלכם פועלות כשצריך אותן." },
                { title: "התאמה אישית", desc: "פתרונות מותאמים ותמיכה ייעודית שצומחת ומסתגלת עם הצרכים הייחודיים של העסק שלכם." },
            ],
            copy: "© 2026 Syslogics. כל הזכויות שמורות.",
            taglineBottom: "IT שעובד עבורכם.ן",
            getStarted: "התחילו היום",
        },
    },
} as const;

export default translations;
